import { useAuth } from '../../../../utils/Auth/AuthProvider';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import type { ReportStudentRow } from '../../../api/types';
import { initialsOf, ShowMoreFooter, SortableTh, Table, TEXT_LINK, Track } from '../../../components/ui';
import ResultCommentModal from './ResultCommentModal';
import LateBadge from '../../../components/LateBadge';
import IntegrityChips from './IntegrityChips';
import { useSortableTable, SortColumn } from '../../../lib/useSortableTable';
import { TH_RULE } from '../../../lib/tableRules';

const PREVIEW_ROWS = 10;

const CELL = 'border-b border-hairline px-2.5 py-3 align-middle';

export default function ReportStudentsTable({ rows, chapterName, examId }: { rows: ReportStudentRow[]; chapterName: Record<number, string>; examId?: number }) {
  const { role } = useAuth();
  const canWrite = role === 'school_admin' || role === 'school' || role === 'admin' || role === 'teacher';
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<ReportStudentRow | null>(null);
  const columns = useMemo<SortColumn<ReportStudentRow>[]>(() => [
    { key: 'rank', accessor: (s) => s.rank, type: 'number' },
    { key: 'name', accessor: (s) => s.name },
    { key: 'section', accessor: (s) => s.section?.name ?? undefined },
    { key: 'score', accessor: (s) => s.score, type: 'number' },
    { key: 'time', accessor: (s) => s.time_minutes, type: 'number' },
    {
      key: 'weakest',
      accessor: (s) => (s.weakest_chapter_id == null ? undefined : chapterName[s.weakest_chapter_id] ?? String(s.weakest_chapter_id)),
    },
  ], [chapterName]);
  // Rank is a server-provided ranking field, so this table's default sort stays
  // rank ascending (rule: derived/ranked tables keep their own default; every
  // other table in this batch defaults to name ascending).
  const { rows: sortedRows, sort, toggle } = useSortableTable(rows, columns, { key: 'rank', dir: 'asc' });
  const needle = search.trim().toLowerCase();
  const [showAll, setShowAll] = useState(false);
  useEffect(() => { setShowAll(false); }, [needle]);
  const filtered = needle ? sortedRows.filter((s) => s.name.toLowerCase().includes(needle)) : sortedRows;
  const visible = showAll ? filtered : filtered.slice(0, PREVIEW_ROWS);

  // The section column only exists when the backend sends it (absent on older backends).
  const hasSection = rows.some((r) => r.section !== undefined);
  const cols = 5 + (hasSection ? 1 : 0) + (examId != null ? 1 : 0);
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <label className="flex min-h-[44px] flex-[0_1_300px] items-center gap-2 rounded-[12px] border border-line-strong bg-surface px-3.5 focus-within:ring-2 focus-within:ring-accent">
          <Search size={18} strokeWidth={1.8} aria-hidden="true" className="flex-none text-muted" />
          <span className="sr-only">Find student</span>
          <input id="report-student-search" type="search" placeholder="Find student…"
                 className="min-w-0 flex-1 border-0 bg-transparent py-2 text-sm text-ink outline-none placeholder:text-muted focus:ring-0"
                 value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
        <span className="text-[13px] text-muted">{filtered.length} of {rows.length} students</span>
      </div>
      <Table minWidth={820}>
        <thead>
          <tr>
            <SortableTh className={`${TH_RULE} w-12`} columnKey="rank" sort={sort} onToggle={toggle} align="right" help="rank">Rank</SortableTh>
            <SortableTh className={TH_RULE} columnKey="name" sort={sort} onToggle={toggle}>Student</SortableTh>
            {hasSection && <SortableTh className={TH_RULE} columnKey="section" sort={sort} onToggle={toggle}>Section</SortableTh>}
            <SortableTh className={`${TH_RULE} w-[180px]`} columnKey="score" sort={sort} onToggle={toggle}>Score</SortableTh>
            <SortableTh className={TH_RULE} columnKey="time" sort={sort} onToggle={toggle} align="right" help="Minutes the student spent on the exam.">Time</SortableTh>
            <SortableTh className={TH_RULE} columnKey="weakest" sort={sort} onToggle={toggle} help="weakest_chapter">Weakest chapter</SortableTh>
            {examId != null && <th scope="col" className={`${TH_RULE} px-2.5 pb-2.5 text-left font-mono text-[11px] font-semibold uppercase tracking-[0.06em] text-muted`}>Comment</th>}
          </tr>
        </thead>
        <tbody>
          {visible.map((s) => (
            <tr key={s.student_id}>
              <td className={`${CELL} text-right font-mono font-medium text-muted`}>{s.awaiting_marking || s.rank == null ? '\u2014' : s.rank}</td>
              <td className={`${CELL} whitespace-nowrap`}>
                <span className="flex items-center gap-2.5">
                  <span aria-hidden="true" className="inline-grid h-8 w-8 flex-none place-items-center rounded-[10px] bg-track text-xs font-semibold text-muted-2">{initialsOf(s.name)}</span>
                  <Link className="font-semibold text-ink no-underline hover:underline" to={`/students/profile/${s.student_id}`}>{s.name}</Link>
                  {s.late && <LateBadge unverified={!!s.late_unverified} />}
                  <IntegrityChips integrity={s.integrity} />
                </span>
              </td>
              {hasSection && <td className={`${CELL} text-muted`}>{s.section?.name ?? '\u2014'}</td>}
              <td className={CELL}>
                {s.awaiting_marking || s.score == null
                  ? <span className="whitespace-nowrap text-xs font-semibold text-warning-ink">Awaiting marking</span>
                  : (
                    <span className="flex items-center gap-2">
                      <Track className="flex-1" value={s.score} tone={s.score < 50 ? 'accent' : 'slate'} label={`${s.name} score`} />
                      <span className="w-12 text-right font-mono font-medium">{`${s.score.toFixed(1)}%`}</span>
                    </span>
                  )}
              </td>
              <td className={`${CELL} text-right font-mono font-medium text-muted`}>{s.time_minutes == null ? '\u2014' : `${s.time_minutes.toFixed(1)} min`}</td>
              <td className={`${CELL} text-[13px]`}>{s.weakest_chapter_id == null ? '\u2014' : chapterName[s.weakest_chapter_id] ?? s.weakest_chapter_id}</td>
              {examId != null && (
                <td className={`${CELL} text-[13px]`}>
                  <span className="flex items-center gap-2">
                    {s.comment && <span className="max-w-[12rem] truncate text-muted" title={s.comment.body}>{s.comment.body}</span>}
                    {canWrite && <button type="button" className={`${TEXT_LINK} !min-h-0 p-0`} onClick={() => setEditing(s)}>
                      {s.comment ? 'Edit' : 'Add'}
                    </button>}
                  </span>
                </td>
              )}
            </tr>
          ))}
          {visible.length === 0 && (
            <tr>
              <td colSpan={cols} className="px-3 py-6 text-center text-sm text-muted">No student matches {'\u201c'}{search.trim()}{'\u201d'}.</td>
            </tr>
          )}
        </tbody>
      </Table>
      <ShowMoreFooter variant="button" showAllLabel="Show all students" shown={visible.length} total={filtered.length} expanded={showAll} onToggle={() => setShowAll((v) => !v)}
        note={<span>Sorted by rank. Names open the student{'\u2019'}s 360 view.</span>} />
      {editing && canWrite && examId != null && (
        <ResultCommentModal key={editing.student_id} examId={examId} studentId={editing.student_id}
          studentName={editing.name} comment={editing.comment ?? null} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}
